import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import { Drawer, Typography, Button, Chip, Divider } from '@mui/material';
import { Icon, BasicTooltip, TableLoader, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import { dateToFormat } from 'src/utils/date';
import useStyles from './styles';

/**
 * Admin view of a company's bill-intake email address (LB-5770): provision it, copy it, rotate it,
 * and read the full alias history with statuses.
 *
 * Email intake is a paid feature, so nothing is provisioned implicitly — not at company creation,
 * not by reading. A company with no address shows the empty state and an explicit Generate action,
 * which is the moment the feature is turned on for them. Both provisioning and rotation are
 * admin-only server-side; this whole page is already admin-gated.
 */

// Backend statuses → house chip variants. Rotated is visually distinct from Expired because a
// rotated address is still routing (inside its grace window) — an operator must be able to tell
// "still works, update the utilities" from "dead".
const STATUS_CHIP_VARIANT = {
  Active: 'success',
  Rotated: 'info',
  Expired: 'inactive'
};
export const BillIntakeAliasDrawer = observer(_ref => {
  let company = _ref.company,
    onClose = _ref.onClose;
  const store = useStore();
  const classes = useStyles();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isLoading = _useState2[0],
    setIsLoading = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    alias = _useState4[0],
    setAlias = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    history = _useState6[0],
    setHistory = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    rotateConfirmOpen = _useState8[0],
    setRotateConfirmOpen = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isProvisioning = _useState0[0],
    setIsProvisioning = _useState0[1];
  const isOpen = !!company;

  // The company whose responses we are still willing to accept. Two awaited calls per fetch means a
  // close or a company switch can land between them, and a late response would otherwise repopulate
  // the drawer with the previous company's address.
  const activeCompanyId = useRef(null);
  const fetchData = async companyId => {
    activeCompanyId.current = companyId;
    setIsLoading(true);
    const aliasResp = await store.company.getBillIntakeAliasAsync.run({
      companyId
    });
    const historyResp = await store.company.getBillIntakeAliasHistoryAsync.run({
      companyId
    });
    if (activeCompanyId.current !== companyId) return;
    setAlias((aliasResp === null || aliasResp === void 0 ? void 0 : aliasResp.response) || null);
    setHistory((historyResp === null || historyResp === void 0 ? void 0 : historyResp.response) || []);
    setIsLoading(false);
  };
  useEffect(() => {
    // Rotation is destructive and reads company at submit time, so a confirmation left open across
    // a switch could rotate a company the admin never opened it for.
    setRotateConfirmOpen(false);
    if (company !== null && company !== void 0 && company.companyId) {
      fetchData(company.companyId);
    } else {
      activeCompanyId.current = null;
      setAlias(null);
      setHistory([]);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company === null || company === void 0 ? void 0 : company.companyId]);
  const handleCopy = address => {
    navigator.clipboard.writeText(address).then(() => {
      toast('success', {
        body: ['Successfully copied to clipboard!']
      });
    }).catch(error => {
      toast('error', {
        body: ['Error copying to clipboard', error]
      });
    });
  };
  const handleProvision = async () => {
    var _resp$response;
    setIsProvisioning(true);
    const resp = await store.company.provisionBillIntakeAliasAsync.run({
      companyId: company.companyId
    });
    setIsProvisioning(false);
    if (!resp || !resp.isSuccessful) return;
    toast('success', {
      body: ["Email intake enabled: ".concat((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.address)]
    });
    await fetchData(company.companyId);
  };
  const handleRotateConfirm = async _ref2 => {
    var _resp$response2;
    let action = _ref2.action;
    setRotateConfirmOpen(false);
    if (action !== 'submit') return;
    const resp = await store.company.rotateBillIntakeAliasAsync.run({
      companyId: company.companyId
    });
    // Repo convention: check isSuccessful and return silently on failure — the async layer surfaces
    // the error toast itself, so a second one here would double up.
    if (!resp || !resp.isSuccessful) return;
    toast('success', {
      body: ["New address issued: ".concat((_resp$response2 = resp.response) === null || _resp$response2 === void 0 ? void 0 : _resp$response2.address)]
    });
    await fetchData(company.companyId);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Drawer, {
    anchor: "right",
    open: isOpen,
    onClose: onClose
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.drawerBody
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, "Bill intake address"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, company === null || company === void 0 ? void 0 : company.companyName), isLoading ? /*#__PURE__*/React.createElement(TableLoader, null) : /*#__PURE__*/React.createElement(React.Fragment, null, alias !== null && alias !== void 0 && alias.address ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.addressRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.addressText
  }, alias.address), /*#__PURE__*/React.createElement(BasicTooltip, {
    title: "Copy to clipboard",
    placement: "top"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    onClick: () => handleCopy(alias.address)
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "document-file-copy"
  })))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    onClick: () => setRotateConfirmOpen(true)
  }, "Rotate address")), /*#__PURE__*/React.createElement(Typography, {
    className: classes.graceNote
  }, "Rotating issues a new address. The current one keeps working for 30 days so the company can update every utility provider they gave it to.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, null, "Email intake is not enabled for this company."), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleProvision,
    disabled: isProvisioning
  }, "Generate address")), /*#__PURE__*/React.createElement(Typography, {
    className: classes.graceNote
  }, "Generating turns email intake on for this company and gives them an address to send bills to. Do this once they are paying for the feature.")), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "History"), history.length ? history.map(item => /*#__PURE__*/React.createElement("div", {
    className: classes.historyRow,
    key: item.tag
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.addressText
  }, item.address), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: item.status,
    variant: STATUS_CHIP_VARIANT[item.status] || 'info'
  }), !!item.rotatedAt && /*#__PURE__*/React.createElement(BasicTooltip, {
    title: "Rotated on",
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.graceNote
  }, dateToFormat(item.rotatedAt))))) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "No history yet.")))), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: "Rotate bill intake address",
    subTitleText: company === null || company === void 0 ? void 0 : company.companyName,
    bodyText: 'The current address keeps working for 30 days. ' + "".concat((company === null || company === void 0 ? void 0 : company.companyName) || 'The company', " must update every utility provider they gave it to. ") + 'This cannot be undone.',
    externalIsOpen: rotateConfirmOpen,
    objectToPassOver: company,
    callBack: handleRotateConfirm,
    customTrigger: null
  }));
});